/**
 * PDF annotations — pure model + (de)serialization + geometry.
 *
 * Local-first & private: the PDF FILE never leaves the device (it is opened
 * from disk into pdf.js); what Folio persists is the ANNOTATION OVERLAY,
 * keyed by the PDF's content fingerprint, as a blob document page
 * (`page` + one `block` of type `pdf-annotations`, the same pattern as
 * workbooks/decks — autosave, version history, trash and sync reused).
 * Re-opening the same file (any device, same account) re-attaches its
 * annotations. Export burns the overlay into a downloadable copy (pdf-lib).
 *
 * All geometry is NORMALIZED to the page box (0..1, top-left origin), so
 * annotations are zoom- and DPI-independent and map cleanly both to the
 * viewer overlay (SVG with a 0..1 viewBox) and to PDF user space (y-flip).
 */
import type { Block } from "@folio/powersync";
import { type EditorBlockNode, PDF_ANNOTATIONS_BLOCK_TYPE } from "@folio/shared";

/** Normalized point: fractions of page width/height, origin at TOP-left. */
export interface NormPoint {
  x: number;
  y: number;
}

/** Normalized rect (top-left origin). */
export interface NormRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface HighlightAnnotation {
  id: string;
  kind: "highlight";
  pageIndex: number;
  rect: NormRect;
  /** Hex color, e.g. "#facc15". */
  color: string;
}

export interface NoteAnnotation {
  id: string;
  kind: "note";
  pageIndex: number;
  at: NormPoint;
  text: string;
  color: string;
}

export interface InkAnnotation {
  id: string;
  kind: "ink";
  pageIndex: number;
  points: NormPoint[];
  color: string;
  /** Stroke width as a fraction of page width (zoom-independent). */
  width: number;
}

export type PdfAnnotation = HighlightAnnotation | NoteAnnotation | InkAnnotation;

export interface PdfAnnotationDoc {
  version: 1;
  /** pdf.js content fingerprint of the annotated file. */
  fingerprint: string;
  /** File name at first annotation (page title / context). */
  fileName: string;
  annotations: PdfAnnotation[];
}

export const HIGHLIGHT_COLOR = "#facc15";
export const NOTE_COLOR = "#f97316";
export const INK_COLOR = "#3b82f6";
export const INK_WIDTH = 0.003;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/** Normalized rect from two drag corners (any order), clamped to the page. */
export function rectFromDrag(a: NormPoint, b: NormPoint): NormRect {
  const x1 = clamp01(Math.min(a.x, b.x));
  const y1 = clamp01(Math.min(a.y, b.y));
  const x2 = clamp01(Math.max(a.x, b.x));
  const y2 = clamp01(Math.max(a.y, b.y));
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
}

export function clampPoint(point: NormPoint): NormPoint {
  return { x: clamp01(point.x), y: clamp01(point.y) };
}

// ── Doc operations (immutable) ──────────────────────────────────────────────

export function emptyPdfAnnotationDoc(fingerprint: string, fileName: string): PdfAnnotationDoc {
  return { version: 1, fingerprint, fileName, annotations: [] };
}

export function addAnnotation(doc: PdfAnnotationDoc, annotation: PdfAnnotation): PdfAnnotationDoc {
  return { ...doc, annotations: [...doc.annotations, annotation] };
}

export function removeAnnotation(doc: PdfAnnotationDoc, id: string): PdfAnnotationDoc {
  return { ...doc, annotations: doc.annotations.filter((a) => a.id !== id) };
}

export function updateNoteText(doc: PdfAnnotationDoc, id: string, text: string): PdfAnnotationDoc {
  return {
    ...doc,
    annotations: doc.annotations.map((a) =>
      a.id === id && a.kind === "note" ? { ...a, text } : a,
    ),
  };
}

export function annotationsOnPage(doc: PdfAnnotationDoc, pageIndex: number): PdfAnnotation[] {
  return doc.annotations.filter((a) => a.pageIndex === pageIndex);
}

// ── (De)serialization doc ↔ content block ───────────────────────────────────

/** Plaintext projection: note texts (search + version diff). */
export function pdfAnnotationsPlainText(doc: PdfAnnotationDoc): string {
  const notes = doc.annotations
    .filter((a): a is NoteAnnotation => a.kind === "note")
    .map((a) => a.text.trim())
    .filter((text) => text.length > 0);
  return [doc.fileName, ...notes].join("\n").trim();
}

export function pdfDocToBlockNode(pageId: string, doc: PdfAnnotationDoc): EditorBlockNode {
  return {
    id: pageId,
    type: PDF_ANNOTATIONS_BLOCK_TYPE,
    content: JSON.stringify(doc),
    text: pdfAnnotationsPlainText(doc),
  };
}

function isNormPoint(value: unknown): value is NormPoint {
  return (
    typeof value === "object" &&
    value !== null &&
    "x" in value &&
    typeof value.x === "number" &&
    "y" in value &&
    typeof value.y === "number"
  );
}

function isNormRect(value: unknown): value is NormRect {
  return (
    typeof value === "object" &&
    value !== null &&
    "x" in value &&
    typeof value.x === "number" &&
    "y" in value &&
    typeof value.y === "number" &&
    "w" in value &&
    typeof value.w === "number" &&
    "h" in value &&
    typeof value.h === "number"
  );
}

function isAnnotation(value: unknown): value is PdfAnnotation {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  if (!("id" in value) || typeof value.id !== "string") {
    return false;
  }
  if (!("pageIndex" in value) || typeof value.pageIndex !== "number") {
    return false;
  }
  if (!("kind" in value)) {
    return false;
  }
  if (value.kind === "highlight") {
    return "rect" in value && isNormRect(value.rect);
  }
  if (value.kind === "note") {
    return (
      "at" in value && isNormPoint(value.at) && "text" in value && typeof value.text === "string"
    );
  }
  if (value.kind === "ink") {
    return (
      "points" in value && Array.isArray(value.points) && value.points.every((p) => isNormPoint(p))
    );
  }
  return false;
}

function isPdfAnnotationDoc(value: unknown): value is PdfAnnotationDoc {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const doc = value as Partial<PdfAnnotationDoc>;
  return (
    typeof doc.fingerprint === "string" &&
    typeof doc.fileName === "string" &&
    Array.isArray(doc.annotations) &&
    doc.annotations.every((a) => isAnnotation(a))
  );
}

/**
 * Parses the content block back into the model. Corrupt/absent content falls
 * back to an empty doc (the viewer always mounts; never breaks on a bad row).
 */
export function blockToPdfDoc(
  block: Pick<Block, "content"> | undefined,
  fingerprint: string,
  fileName: string,
): PdfAnnotationDoc {
  if (!block?.content) {
    return emptyPdfAnnotationDoc(fingerprint, fileName);
  }
  try {
    const parsed: unknown = JSON.parse(block.content);
    if (isPdfAnnotationDoc(parsed)) {
      return parsed;
    }
  } catch {
    // fall through to empty
  }
  return emptyPdfAnnotationDoc(fingerprint, fileName);
}
